import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { keepPreviousData } from "@tanstack/react-query";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";
import { ChevronRightIcon } from "@heroicons/react/16/solid";
import { Pin } from "lucide-react";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { supplierListQuery } from "@/app/Suppliers/GraphQL/supplierListQuery";
import type { SupplierListItem } from "@/app/Suppliers/GraphQL/supplierListQuery";
import { SupplierIcon } from "@/app/Suppliers/Components/SupplierIcon";
import { usePinnedSuppliers } from "@/app/Suppliers/Utils/usePinnedSuppliers";
import { cn } from "@/lib/utils";

export function SettingsSupplierSelectScreen() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const { isPinned, togglePin, partitionByPinned } = usePinnedSuppliers();

  const { data } = useGqlQuery(
    supplierListQuery,
    {
      first: 200,
      page: 1,
      archived: false,
      search: search || undefined,
    },
    { placeholderData: keepPreviousData },
  );
  const suppliers = data?.suppliers.data ?? [];
  const { pinned, unpinned } = partitionByPinned(suppliers);

  function selectSupplier(id: string) {
    navigate({
      from: "/$tenant/$orgId/supplier/{-$supplierId}",
      to: "/$tenant/$orgId/supplier/{-$supplierId}/settings",
      params: (prev) => prev,
      search: { supplier: id },
    });
  }

  function renderRow(supplier: SupplierListItem) {
    const pinnedSupplier = isPinned(supplier.id);

    return (
      <li key={supplier.id} className="flex items-center">
        <button
          type="button"
          onClick={() => togglePin(supplier.id)}
          aria-label={pinnedSupplier ? "Unpin supplier" : "Pin supplier"}
          aria-pressed={pinnedSupplier}
          className={cn(
            "py-3 pl-6 pr-1 transition-colors focus:outline-none",
            pinnedSupplier
              ? "text-indigo-600"
              : "text-gray-300 hover:text-gray-500 focus-visible:text-gray-500",
          )}
        >
          <Pin className={cn("size-4", pinnedSupplier ? "fill-current" : "")} />
        </button>
        <button
          type="button"
          onClick={() => selectSupplier(supplier.id)}
          className="group flex min-w-0 flex-1 items-center gap-3 py-3 pl-3 pr-6 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-50"
        >
          <SupplierIcon
            model={supplier}
            className="size-9 shrink-0 rounded-md border border-gray-200 object-contain"
          />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-900">
              {supplier.name}
            </p>
            {supplier.email ? (
              <p className="truncate text-xs text-gray-500">{supplier.email}</p>
            ) : null}
          </div>
          <ChevronRightIcon className="size-4 shrink-0 text-gray-300 transition-colors group-hover:text-gray-500" />
        </button>
      </li>
    );
  }

  function renderHeading(label: string, className?: string) {
    return (
      <li
        className={cn(
          "bg-gray-50 px-6 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500",
          className,
        )}
      >
        {label}
      </li>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-sm">
      <div className="border-b border-gray-100 px-6 py-5">
        <h2 className="text-base font-semibold text-gray-900">
          Select a supplier
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          Choose which supplier’s settings you want to manage. Your global
          supplier selection stays unchanged.
        </p>
        <div className="relative mt-4 max-w-sm">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search suppliers..."
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
      </div>

      {suppliers.length === 0 ? (
        <div className="px-6 py-12 text-center text-sm text-gray-500">
          No suppliers found.
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {pinned.length > 0 ? (
            <>
              {renderHeading("Pinned")}
              {pinned.map(renderRow)}
              {unpinned.length > 0 ? renderHeading("All suppliers") : null}
            </>
          ) : null}
          {unpinned.map(renderRow)}
        </ul>
      )}
    </div>
  );
}
